// The organization's report logo: the prompt to set one, and the card to
// manage it.
//
// An MSSP hands its customers the exported PDF under its own brand, and this
// is where that brand enters the platform: the printed report's cover shows
// the image in place of the platform's wordmark, beside a "Powered by Foresee
// Security" line. The management tier and platform admins change it,
// which the server enforces; the pages show these to nobody else, because a
// row of buttons a member cannot press is noise.
//
// Two surfaces, one upload path. The dashboard carries a PROMPT that exists
// only until a logo is set - a call to finish setting the organization up,
// gone the moment it is done. The organization's Settings page carries the
// CARD that shows what is set and replaces or removes it.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Loader2, Trash2, Upload } from 'lucide-react';
import { type ChangeEvent, type ReactNode, useRef } from 'react';
import { useToast } from '@/context/ToastContext';
import { api, type User } from '@/lib/api';
import { orgLogoKey, useOrgLogo } from './useOrgLogo';

/** What the server accepts. Restated here so the obvious mistakes get an
 *  answer before a round trip; the server stays the authority. */
const ACCEPTED_TYPES = new Set(['image/png', 'image/jpeg']);
const MAX_BYTES = 1 << 20;

/** Whether `user` may set the org's logo: a platform admin, or a member at
 *  the management tier. Mirrors the server's write gate. */
export function canBrandOrg(user: User | null, orgId: string): boolean {
  if (!user) return false;
  if (user.is_admin) return true;
  const role = user.memberships?.find((m) => m.org_id === orgId)?.role;
  return role === 'manager' || role === 'org_admin';
}

/** The upload and removal, shared by the prompt and the card. */
function useLogoActions(orgId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = () => qc.invalidateQueries({ queryKey: orgLogoKey(orgId) });
  const upload = useMutation({
    mutationFn: (file: File) => api.orgSetLogo(orgId, file),
    onSuccess: () => {
      toast.success('Organization label saved');
      void refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: () => api.orgDeleteLogo(orgId),
    onSuccess: () => {
      toast.success('Organization label removed');
      void refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function pick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Cleared so picking the same file after a refusal fires change again.
    e.target.value = '';
    if (!file) return;
    if (!ACCEPTED_TYPES.has(file.type)) {
      toast.error('The label must be a PNG or JPEG image');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('The label must be 1 MB or smaller');
      return;
    }
    upload.mutate(file);
  }

  return { upload, remove, pick, busy: upload.isPending || remove.isPending };
}

/** A button that opens the file picker, with the picker beside it. */
function LogoPicker({
  onPick,
  disabled,
  pending,
  className,
  children,
}: {
  onPick: (e: ChangeEvent<HTMLInputElement>) => void;
  disabled: boolean;
  pending: boolean;
  className: string;
  children: ReactNode;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        className={className}
        disabled={disabled}
        onClick={() => fileRef.current?.click()}
      >
        {pending ? <Loader2 size={16} className="animate-spin" aria-hidden /> : <Upload size={16} aria-hidden />}
        {children}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        tabIndex={-1}
        aria-hidden
        onChange={onPick}
      />
    </>
  );
}

/**
 * The dashboard's prompt to set a label. Renders nothing while the answer is
 * still loading and nothing once a logo exists: it is a step to finish, not
 * a fixture of the page.
 */
export function OrgLogoPrompt({ orgId }: { orgId: string }) {
  const { url, isPending } = useOrgLogo(orgId);
  const { upload, pick, busy } = useLogoActions(orgId);
  if (isPending || url !== null) return null;
  return (
    <div className="card mb-6 p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-4 min-w-0">
        <div
          className="shrink-0 p-3 rounded-xl"
          style={{ background: 'var(--status-info-bg)', color: 'var(--status-info)' }}
        >
          <ImagePlus size={24} aria-hidden />
        </div>
        <div className="min-w-0">
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600 }}>Set your organization label</h3>
          <p className="text-muted text-sm mt-1">
            It will be used to white-label your exported reports. PNG or JPEG, up to 1 MB.
          </p>
        </div>
      </div>
      <LogoPicker onPick={pick} disabled={busy} pending={upload.isPending} className="btn-primary">
        Upload label
      </LogoPicker>
    </div>
  );
}

/** The Settings page's card: what is set, and the controls to change it. */
export function OrgLogoCard({ orgId }: { orgId: string }) {
  const { url, isPending } = useOrgLogo(orgId);
  const { upload, remove, pick, busy } = useLogoActions(orgId);
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 mb-1">
        <ImagePlus className="w-4 h-4 text-ink-500" aria-hidden />
        <h3 className="text-base font-semibold text-ink-100">Organization label</h3>
      </div>
      <p className="text-xs text-ink-500 mb-4">
        Printed on exported PDF reports in place of the Foresee mark, so they go out under your
        brand. PNG or JPEG, up to 1 MB.
      </p>
      <div className="flex flex-wrap items-center gap-4">
        {/* The report's own light scope, because that is the ground the logo
            prints on: a mark previewed on the app's dark surface would look
            nothing like the PDF. */}
        <div className="brand-scope flex h-16 w-40 shrink-0 items-center justify-center rounded-lg border border-case-line bg-case-page p-2">
          {url ? (
            <img src={url} alt="Organization label" className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="text-xs text-case-ink-mute">{isPending ? '' : 'No label set'}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <LogoPicker onPick={pick} disabled={busy} pending={upload.isPending} className="btn-primary">
            {url ? 'Replace' : 'Upload'}
          </LogoPicker>
          {url && (
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={() => remove.mutate()}
            >
              {remove.isPending ? (
                <Loader2 size={16} className="animate-spin" aria-hidden />
              ) : (
                <Trash2 size={16} aria-hidden />
              )}
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
